// src/views/UserViews/insights/components/ComplianceTrendChart.tsx
//
// LineChart of protected reporting time % over the selected window - carries a single series
// only (mute % stays cards-only, SafetySection).
//
// Single-day ranges hide the chart entirely (replaced with text) rather than plot a one-point
// "trend" - keyed off the resolved range's `from`/`to`, not the preset name, so a single-day
// custom range hides too. `getComplianceTrend` is still fetched at every range (Safety row's
// headline ratios are recomputed from the same rows) - only this component's own render is
// skipped.

import { Box, Button, Divider, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { LineChart, lineClasses } from '@mui/x-charts/LineChart';
import { StringKeys } from '@/resources/strings/StringKeys';
import COLORS from '@/resources/style/Themes';
import type { InsightsSectionState } from '@/hooks/insights/useInsightsData';
import type {
  CompliancePoint,
  ComplianceTrendResult,
  FeatureComplianceSummary,
} from '@/models/Insights/InsightsTypes';
import type { InsightsDateRange } from '@/models/Insights/insightsDateRange';
import SectionCard from './SectionCard';
import FeatureComplianceSection from './FeatureComplianceSection';

/** Explicit height is required (not tuning) - MUI X v8 charts without `height` inside an
 * unbounded ancestor chain (this page has none) hit a ResizeObserver feedback loop
 * (mui/mui-x#12263) and grow unbounded.
 *
 * 320px fits a 0-100% axis with five ticks plus the per-feature strip below (`SectionCard`'s
 * `autoHeight`) - not sized for parity with `DeviceStatusDonut` (that card instead sizes to
 * match this one). Verified in a real browser render that the "100%" tick and final x-axis
 * label both stay inside the SVG at this height - see
 * `ComplianceTrendChartAxisLayout.test.tsx`. */
const CHART_HEIGHT = 320;
const RATIO_TO_PERCENT = 100;
/** Matches `MetricCard`'s constant of the same name so the tooltip's percent reads at the same
 * precision as the Safety cards plotting the same measure. */
const PERCENT_DECIMAL_PLACES = 1;
const TITLE_DATE_FORMAT = 'MMM D, YYYY';
const EMPTY_HEADLINE = '\u2014';
/** Shared by `ratio` and `latestRatio` so they read as size peers (see `ComplianceHeadline`'s
 * header) - color is the only deliberate asymmetry between them. */
const COMPLIANCE_STAT_FONT_SIZE = '2.25rem'; // 36px
/** Pinned to a fixed 0-100 domain rather than auto-scaled - auto-scaling to the series' own
 * min/max would exaggerate small day-to-day swings. */
const Y_AXIS_MIN = 0;
const Y_AXIS_MAX = 100;
const Y_AXIS_TICKS = [0, 25, 50, 75, 100];
/** Roughly this many labels render on the x-axis regardless of range length - see
 * `shouldRenderXAxisTick`. */
const MAX_X_AXIS_TICKS = 8;
/** Right margin sized so the final x-axis label ("MMM D", ~40px) fits inside the SVG - see
 * the `margin` prop's comment for the measurement behind this value. */
const X_AXIS_LAST_LABEL_MARGIN = 24;
const LINE_STROKE_WIDTH = 2;
/** Needs 2+ real points to compare - same threshold as `isSingleDayRange`, checked
 * independently since a multi-day range can still be an all-gap window (which
 * `isSingleDayRange` can't see - it only looks at `range`, never `data.points`). */
const MIN_POINTS_FOR_COMPLIANCE_DELTA = 2;
const RATIO_TO_PERCENTAGE_POINTS = 100;
const DELTA_ARROW_UP = '\u25B2';
const DELTA_ARROW_DOWN = '\u25BC';
// 1.25x subtitle1 (20px) - deliberately smaller than an original 2x (32px) idea, which read
// oversized next to SectionCard's 16px title. Shared by all three Typography sites below
// (zero/arrow/magnitude) so sizes match; only `fontWeight` differs for the zero branch.
const DELTA_FONT_SIZE = '1.25rem'; // 20px
// `1` (no extra leading) fixes the arrow sitting higher than the magnitude's baseline at the
// default subtitle1 line-height (1.75) - unitless, so any shared fontSize+lineHeight pair
// stays aligned regardless of `DELTA_FONT_SIZE`. Verified via bounding-rect assertion in
// ComplianceTrendChart.test.tsx's delta-chip suite.
const DELTA_LINE_HEIGHT = 1;

/** `null` passes through unchanged so a day with no rollup row renders as a gap - never
 * substitute `0` for a missing day. */
function toPercentOrNull(ratio: number | null): number | null {
  return ratio === null ? null : ratio * RATIO_TO_PERCENT;
}

/** Pure extraction of the chart's series values, exported so tests can assert the
 * null-vs-zero distinction directly against `LineChart`'s input. */
export function buildComplianceSeriesValues(
  points: ComplianceTrendResult['points'],
): (number | null)[] {
  return points.map((point) => toPercentOrNull(point.complianceRatio));
}

/** A single-day window (`from === to`) renders as one dot, not a trend, for any preset or
 * custom range; a `null` range has nothing to plot either. */
export function isSingleDayOrUnresolvedRange(range: InsightsDateRange | null): boolean {
  return range === null || range.from === range.to;
}

/** Narrower than `isSingleDayOrUnresolvedRange` - true only for a resolved single-day range,
 * never `null`. Distinguishes the two empty states below (one has a date + escape hatch, the
 * other has neither). */
export function isSingleDayRange(range: InsightsDateRange | null): boolean {
  return range !== null && range.from === range.to;
}

function hasResolvedComplianceRatio(
  point: CompliancePoint,
): point is CompliancePoint & { complianceRatio: number } {
  return point.complianceRatio !== null;
}

/** The last point chronologically among RESOLVED points, not `points[length - 1]` (which can
 * be a trailing null-ratio gap day - Rule 1 forbids treating that as "the latest reading").
 * Shared by `computeComplianceDelta` and `getLatestComplianceRatio`, exported directly so
 * this filter+last-index logic is unit-testable. */
export function getLastResolvedCompliancePoint(
  points: ComplianceTrendResult['points'],
): (CompliancePoint & { complianceRatio: number }) | null {
  const resolvedPoints = points.filter(hasResolvedComplianceRatio);
  return resolvedPoints.length > 0 ? resolvedPoints[resolvedPoints.length - 1] : null;
}

/** The most recent RESOLVED day's own ratio - a distinct, smaller stat next to
 * `ComplianceHeadline`'s primary sum-over-sum window ratio (see
 * `InsightsDAO.getComplianceTrend`'s header: "sum-over-sum, not an average of daily
 * ratios"). Returns `null` (Rule 1) when there are zero resolved points - never a fake 0%. */
export function getLatestComplianceRatio(
  points: ComplianceTrendResult['points'],
): number | null {
  const latestPoint = getLastResolvedCompliancePoint(points);
  return latestPoint ? latestPoint.complianceRatio : null;
}

/** Movement from the first to last NON-NULL point in the selected range, in whole percentage
 * points - never a prior/equal-length-window comparison (would need a second query, out of
 * scope). Rounds to a whole number since the chip is a coarse trend signal, not a precise
 * measurement.
 *
 * Returns `null` (Rule 1) when fewer than two real points exist to compare. Callers must
 * treat `null` as "render nothing" - a genuine zero delta is a distinct valid return. */
export function computeComplianceDelta(
  points: ComplianceTrendResult['points'],
): number | null {
  const resolvedPoints = points.filter(hasResolvedComplianceRatio);
  if (resolvedPoints.length < MIN_POINTS_FOR_COMPLIANCE_DELTA) {
    return null;
  }

  const first = resolvedPoints[0];
  const last = getLastResolvedCompliancePoint(points);
  if (last === null) {
    // Unreachable given the length check above - narrows `last` for TypeScript.
    return null;
  }
  return Math.round(
    (last.complianceRatio - first.complianceRatio) * RATIO_TO_PERCENTAGE_POINTS,
  );
}

/** Roughly `MAX_X_AXIS_TICKS` evenly spaced labels regardless of range length. First and last
 * point always render so the range's bounds are unambiguous; the interior run is spaced
 * backwards from the last point so that guarantee survives to the rendered axis. */
export function shouldRenderXAxisTick(index: number, pointCount: number): boolean {
  if (pointCount <= 0) {
    return false;
  }
  if (index === 0 || index === pointCount - 1) {
    return true;
  }
  if (pointCount <= MAX_X_AXIS_TICKS) {
    return true;
  }
  const step = Math.ceil(pointCount / MAX_X_AXIS_TICKS);
  // Anchored to the LAST index (not the first) so the evenly spaced run lands ON the final
  // point - anchoring to index 0 left an uneven last gap that x-charts' `tickLabelInterval:
  // 'auto'` collision avoidance dropped, silently hiding the final date label (confirmed via
  // real-browser SVG text metrics, not jsdom). Fixing the tick *spacing* here (rather than
  // overriding `tickLabelInterval` directly) preserves that adaptive collision avoidance,
  // which a narrow (343px) card width still depends on to avoid overlapping labels.
  return (pointCount - 1 - index) % step === 0;
}

type ComplianceTrendChartProps = {
  trend: InsightsSectionState<ComplianceTrendResult>;
  range: InsightsDateRange | null;
  /** Switches the header preset to '7days' from the single-day empty state's escape hatch.
   * Optional - a caller with nowhere to route the click gets a quiet empty state, not a dead
   * button. */
  onViewSevenDays?: () => void;
  /** The per-feature (arc/fall/head) compliance strip, stacked below this chart inside the
   * same `SectionCard` - see `renderFeatureComplianceBlock`'s comment for when it renders.
   * Optional, same reasoning as `onViewSevenDays`. */
  featureCompliance?: InsightsSectionState<FeatureComplianceSummary>;
};

/**
 * The per-feature compliance strip, rendered as a second child inside the SAME `SectionCard`
 * as this chart (not a separate section - see Insights.tsx). Reuses `FeatureComplianceSection`
 * directly; that component still owns its own four correctness rules in its own header
 * comment - relocating where it renders must never touch those.
 *
 * Called from the single-day branch but NOT the unresolved-range one: `getFeatureCompliance`
 * is a separate query keyed on `orgCode`/`range` (useInsightsData.ts), and a single day still
 * has a valid per-feature score even with no line-chart trend to plot. A `null` range has no
 * date window to query at all, so `featureCompliance` would never carry range-matched data -
 * omitting the strip there keeps this component in sync with what the data layer fetches.
 */
function renderFeatureComplianceBlock(
  featureCompliance: InsightsSectionState<FeatureComplianceSummary> | undefined,
) {
  if (!featureCompliance) return null;

  // No margin here - the `Divider` between the chart and this block now owns that spacing
  // (see the default export below).
  return <FeatureComplianceSection featureCompliance={featureCompliance} />;
}

/** Renders TWO numbers in one flex row (name predates that; rename out of scope). Both share
 * `COMPLIANCE_STAT_FONT_SIZE` as size peers - color is the one asymmetry: `ratio` stays
 * `primary.main` (the more "official" sum-over-sum figure), `latestRatio` stays `text.primary`
 * (a real-time pulse check on it). RENDER ORDER: `latestRatio` (when supplied) renders
 * first/left, `ratio` second/right - visual only, doesn't change `isEmpty` or omission rules.
 *
 * `ratio`: whole-window sum-over-sum ratio (see `ComplianceTrendResult`'s header). `null`
 * renders an em dash, never stale/zeroed (Rule 1) - both empty-state branches below pass this.
 * The sublabel plays `MetricCard.label`'s role, not `.caption`'s, so it stays outside the
 * hover-reveal restyle.
 *
 * `latestRatio` (optional): see `getLatestComplianceRatio`'s comment for why it differs from
 * `ratio`. `undefined` = no latest-day figure applies (empty-state branches); `null` =
 * explicitly looked, found zero resolved points (Rule 1) - same em dash, own label. */
function ComplianceHeadline({
  ratio,
  latestRatio,
}: {
  ratio: number | null;
  latestRatio?: number | null;
}) {
  const { t } = useTranslation();
  const isEmpty = ratio === null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap' }}>
      {latestRatio !== undefined && (
        <Box>
          <Typography
            component="p"
            data-testid={latestRatio === null ? 'compliance-latest-empty' : 'compliance-latest-value'}
            // `text.primary`, not `primary.main` - see this function's header comment.
            sx={{ fontSize: COMPLIANCE_STAT_FONT_SIZE, fontWeight: 700, lineHeight: 1.1, color: 'text.primary' }}
          >
            {latestRatio === null
              ? EMPTY_HEADLINE
              : `${(latestRatio * RATIO_TO_PERCENT).toFixed(PERCENT_DECIMAL_PLACES)}%`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(StringKeys.INSIGHTS_TREND_LATEST_LABEL)}
          </Typography>
        </Box>
      )}
      <Box>
        <Typography
          component="p"
          data-testid={isEmpty ? 'compliance-headline-empty' : 'compliance-headline-value'}
          sx={{ fontSize: COMPLIANCE_STAT_FONT_SIZE, fontWeight: 700, lineHeight: 1.1, color: 'primary.main' }}
        >
          {isEmpty ? EMPTY_HEADLINE : `${(ratio * RATIO_TO_PERCENT).toFixed(PERCENT_DECIMAL_PLACES)}%`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(StringKeys.INSIGHTS_PROTECTED_REPORTING_TIME_LABEL)}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Renders `computeComplianceDelta`'s result as an arrow + magnitude + "pts" chip, passed as
 * `SectionCard`'s `action` prop on the main populated branch only - the hiding rules live in
 * the caller's decision to render this component at all.
 *
 * The arrow is the ONLY sign indicator (task spec): a negative delta shows its absolute value
 * next to a down arrow rather than double-encoding direction with a minus sign. An
 * exactly-zero delta gets neither arrow.
 */
function ComplianceDeltaChip({ delta }: { delta: number }) {
  const { t } = useTranslation();

  if (delta === 0) {
    return (
      <Typography
        component="span"
        variant="subtitle1"
        color="text.secondary"
        data-testid="compliance-delta-chip"
        sx={{ fontSize: DELTA_FONT_SIZE, lineHeight: DELTA_LINE_HEIGHT }}
      >
        {t(StringKeys.INSIGHTS_TREND_DELTA_PTS, { delta: 0 })}
      </Typography>
    );
  }

  const isPositive = delta > 0;

  return (
    <Box
      data-testid="compliance-delta-chip"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        color: isPositive ? 'success.main' : 'error.main',
      }}
    >
      <Typography
        component="span"
        variant="subtitle1"
        sx={{ fontWeight: 700, fontSize: DELTA_FONT_SIZE, lineHeight: DELTA_LINE_HEIGHT }}
        aria-hidden
        data-testid={isPositive ? 'compliance-delta-arrow-up' : 'compliance-delta-arrow-down'}
      >
        {isPositive ? DELTA_ARROW_UP : DELTA_ARROW_DOWN}
      </Typography>
      <Typography
        component="span"
        variant="subtitle1"
        sx={{ fontWeight: 700, fontSize: DELTA_FONT_SIZE, lineHeight: DELTA_LINE_HEIGHT }}
      >
        {t(StringKeys.INSIGHTS_TREND_DELTA_PTS, { delta: Math.abs(delta) })}
      </Typography>
    </Box>
  );
}

export default function ComplianceTrendChart({
  trend,
  range,
  onViewSevenDays,
  featureCompliance,
}: ComplianceTrendChartProps) {
  const { t } = useTranslation();

  // Redundant with `isSingleDayOrUnresolvedRange`'s own null handling, but spelled out here
  // so TypeScript narrows `range` for the rest of this function. Both empty branches below
  // show the real title via SectionCard (a titleless card read as broken next to the
  // always-titled donut card).
  if (!range) {
    // No `renderFeatureComplianceBlock` here - see that function's comment for why an
    // unresolved range is the one case that omits the strip.
    return (
      <SectionCard title={t(StringKeys.INSIGHTS_TREND_TITLE)}>
        {/* `latestRatio` left `undefined` - see `ComplianceHeadline`'s header for what that
         * means; this branch has nothing meaningful to show there. */}
        <ComplianceHeadline ratio={null} />
        <Box
          sx={{
            minHeight: CHART_HEIGHT,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="compliance-trend-unresolved-range"
          >
            {t(StringKeys.INSIGHTS_TREND_EMPTY_UNRESOLVED)}
          </Typography>
        </Box>
      </SectionCard>
    );
  }

  if (isSingleDayRange(range)) {
    return (
      // `autoHeight`: the strip renders below this branch's empty-state Box too, so it needs
      // the same stretch opt-out as the normal-range branch below.
      <SectionCard title={t(StringKeys.INSIGHTS_TREND_TITLE)} autoHeight={Boolean(featureCompliance)}>
        {/* `latestRatio` omitted, same as the unresolved-range branch above. */}
        <ComplianceHeadline ratio={null} />
        <Box
          sx={{
            minHeight: CHART_HEIGHT,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
          data-testid="compliance-trend-hidden"
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t(StringKeys.INSIGHTS_TREND_EMPTY_SINGLE_DAY_TITLE)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(StringKeys.INSIGHTS_TREND_EMPTY_SINGLE_DAY_BODY, {
              date: dayjs(range.from).format(TITLE_DATE_FORMAT),
            })}
          </Typography>
          {onViewSevenDays && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={onViewSevenDays}
                data-testid="compliance-trend-view-7-days"
              >
                {t(StringKeys.INSIGHTS_TREND_EMPTY_VIEW_7_DAYS)}
              </Button>
            </Box>
          )}
        </Box>
        {/* Shown here (unlike `!range` above) - a single day still has a valid per-feature
         * score even with no trend to plot. See `renderFeatureComplianceBlock`'s comment. */}
        {renderFeatureComplianceBlock(featureCompliance)}
      </SectionCard>
    );
  }

  const { data, loading, error } = trend;
  const pointCount = data?.points.length ?? 0;
  // `null` while loading/erroring/data-less, not just on a genuine gap - a chip/figure
  // computed from not-yet-fetched or failed `data` would be stale or fabricated.
  const delta = data ? computeComplianceDelta(data.points) : null;
  const latestRatio = data ? getLatestComplianceRatio(data.points) : null;

  return (
    // `autoHeight`: with `featureCompliance` supplied, this card renders two stacked children
    // (chart + strip) - as a stretched grid item, the chart's own `flex: 1` would consume the
    // full stretched height and push the strip past the card's bottom edge, where `overflow:
    // hidden` clips it (see ComplianceTrendChartCardOverflow.test.tsx). Opting out lets the
    // card size to its own content instead. Gated on `featureCompliance` since a single-child
    // card never had this bug.
    <SectionCard
      title={t(StringKeys.INSIGHTS_TREND_TITLE)}
      autoHeight={Boolean(featureCompliance)}
      action={delta !== null ? <ComplianceDeltaChip delta={delta} /> : undefined}
    >
      <ComplianceHeadline ratio={data?.headlineComplianceRatio ?? null} latestRatio={latestRatio} />

      {loading && (
        <Skeleton
          variant="rectangular"
          sx={{ flex: 1, minHeight: CHART_HEIGHT }}
          data-testid="compliance-trend-loading"
        />
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" data-testid="compliance-trend-error">
          {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
        </Typography>
      )}

      {!loading && !error && data && (
        // `flex: 1` fills whatever height SectionCard's flex column hands this card;
        // `justifyContent: 'center'` centers the fixed-height LineChart within that space -
        // absorbs any leftover stretch height as centering, not dead space at the bottom.
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <LineChart
            height={CHART_HEIGHT}
            hideLegend
            grid={{ horizontal: true }}
            // `top: 20` gives the "100%" tick label room to render (clipped at smaller
            // margins, real-browser verified). `right` is the second half of the
            // missing-last-date fix (`shouldRenderXAxisTick` is the first half) - the final
            // label needs about half its own width of margin to draw at all. `bottom: 20`
            // gives the tick + date label room without extra dead space before the strip -
            // see ComplianceTrendChartAxisLayout.test.tsx.
            margin={{ left: 40, right: X_AXIS_LAST_LABEL_MARGIN, top: 20, bottom: 20 }}
            // Targets the rendered line path's DOM class - `lineClasses` is x-charts' own
            // exported selector, so it survives an internal class-name change upstream.
            sx={{
              [`& .${lineClasses.line}`]: { strokeWidth: LINE_STROKE_WIDTH },
            }}
            xAxis={[
              {
                data: data.points.map((point) => point.date),
                scaleType: 'point',
                valueFormatter: (date) => dayjs(date).format('MMM D'),
                tickInterval: (_value, index) => shouldRenderXAxisTick(index, pointCount),
              },
            ]}
            yAxis={[
              {
                min: Y_AXIS_MIN,
                max: Y_AXIS_MAX,
                tickInterval: Y_AXIS_TICKS,
                valueFormatter: (value: number) => `${value}%`,
                disableLine: true,
                disableTicks: true,
              },
            ]}
            series={[
              {
                data: buildComplianceSeriesValues(data.points),
                // Kept even with `hideLegend` - the tooltip still reads this label.
                label: t(StringKeys.INSIGHTS_PROTECTED_REPORTING_TIME_LABEL),
                color: COLORS.SAFEGUARD_BLUE,
                // Daily samples, not continuous - no smoothing curve between real days.
                curve: 'linear',
                // Never show marks - a long series would render as overlapping circles.
                showMark: false,
                // Explicit `false` (also the default) - a gap must never render as an
                // interpolated bridge.
                connectNulls: false,
                // `null` formats to '' - a missing day must never show as "0.0%".
                valueFormatter: (value) =>
                  value === null ? '' : `${value.toFixed(PERCENT_DECIMAL_PLACES)}%`,
              },
            ]}
          />
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Independent of loading/error/data above - `getFeatureCompliance` is its own query
       * (see `renderFeatureComplianceBlock`'s comment) and must not gate on this chart's
       * state. */}
      {renderFeatureComplianceBlock(featureCompliance)}
    </SectionCard>
  );
}
